import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, 
  DialogFooter, DialogDescription 
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../ui/select';
import { 
  Plus, Search, Filter, TrendingUp, TrendingDown, Minus,
  Edit2, Trash2, MoreVertical, ChevronRight, Target,
  DollarSign, Users, Settings, GraduationCap, Calendar,
  BarChart3, Eye, Copy, Archive
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '../ui/dropdown-menu';
import { bscPerspectives, mockObjectives } from '../../mock/strategyData';
import { mockCompanies } from '../../mock/data';
import SectionHeader from '../shared/SectionHeader';

// Icon map
const iconMap = {
  DollarSign, Users, Settings, GraduationCap
};

// Status config
const statusConfig = {
  'exceeding': { label: 'Exceeding', color: 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30' },
  'on-track': { label: 'On Track', color: 'bg-blue-500/20 text-blue-500 border-blue-500/30' },
  'at-risk': { label: 'At Risk', color: 'bg-amber-500/20 text-amber-500 border-amber-500/30' },
  'below-target': { label: 'Below Target', color: 'bg-orange-500/20 text-orange-500 border-orange-500/30' },
  'critical': { label: 'Critical', color: 'bg-red-500/20 text-red-500 border-red-500/30' }
};

// KPI Card Component
const KPICard = ({ kpi, perspective, entity, onClick, onEdit, onDuplicate, onArchive }) => {
  const achievement = kpi.target > 0 ? Math.round((kpi.actual / kpi.target) * 100) : 0;
  const TrendIcon = kpi.trend === 'up' ? TrendingUp : kpi.trend === 'down' ? TrendingDown : Minus;
  const trendColor = kpi.trend === 'up' ? 'text-emerald-500' : kpi.trend === 'down' ? 'text-red-500' : 'text-muted-foreground';
  const PerspectiveIcon = iconMap[perspective?.icon] || Target;
  
  const getAchievementColor = (ach) => {
    if (ach >= 100) return 'from-emerald-500 to-emerald-600';
    if (ach >= 90) return 'from-blue-500 to-blue-600';
    if (ach >= 75) return 'from-amber-500 to-amber-600';
    return 'from-red-500 to-red-600';
  };

  return (
    <Card 
      className="group hover:shadow-lg transition-all cursor-pointer border-border/50 hover:border-primary/30"
      data-testid={`kpi-card-${kpi.id}`}
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${perspective?.color}20` }}
            >
              <PerspectiveIcon className="w-4 h-4" style={{ color: perspective?.color }} />
            </div>
            <Badge variant="outline" className={statusConfig[kpi.status]?.color || ''}>
              {statusConfig[kpi.status]?.label || kpi.status}
            </Badge>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onClick(kpi); }}>
                <Eye className="w-4 h-4 mr-2" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(kpi); }}>
                <Edit2 className="w-4 h-4 mr-2" />
                Edit KPI
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDuplicate(kpi); }}>
                <Copy className="w-4 h-4 mr-2" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onArchive(kpi); }} className="text-red-500">
                <Archive className="w-4 h-4 mr-2" />
                Archive
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* KPI Name */}
        <div onClick={() => onClick(kpi)}>
          <h4 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
            {kpi.name}
          </h4>
          <p className="text-xs text-muted-foreground mb-3">{entity?.name || 'Enterprise'}</p>

          {/* Values */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="text-center p-2 rounded-lg bg-muted/30">
              <p className="text-xs text-muted-foreground">Actual</p>
              <p className="text-sm font-bold text-foreground">{kpi.actual}{kpi.unit}</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-muted/30">
              <p className="text-xs text-muted-foreground">Target</p>
              <p className="text-sm font-bold text-foreground">{kpi.target}{kpi.unit}</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-muted/30">
              <p className="text-xs text-muted-foreground">Weight</p>
              <p className="text-sm font-bold text-foreground">{kpi.weight}%</p>
            </div>
          </div>

          {/* Achievement */}
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground">Achievement</span>
                <div className="flex items-center gap-1">
                  <TrendIcon className={`w-3 h-3 ${trendColor}`} />
                  <span className="font-bold text-foreground">{achievement}%</span>
                </div>
              </div>
              <Progress 
                value={Math.min(achievement, 100)} 
                className="h-2"
                indicatorClassName={`bg-gradient-to-r ${getAchievementColor(achievement)}`}
              />
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-3 pt-3 border-t border-border/50 flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 text-xs"
            onClick={(e) => { e.stopPropagation(); onClick(kpi); }}
          >
            <Calendar className="w-3 h-3 mr-1" />
            Monthly Data
          </Button>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );
};

// Create/Edit KPI Dialog
const KPIDialog = ({ open, onClose, kpi, onSave }) => {
  const [formData, setFormData] = useState(kpi || {
    name: '',
    description: '',
    perspectiveId: 'financial',
    entityId: 'enterprise',
    entityType: 'enterprise',
    unit: '%',
    weight: 10,
    target: 100,
    actual: 0,
    formula: '',
    dataSource: '',
    owner: '',
    frequency: 'monthly'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      id: kpi?.id || `kpi-${Date.now()}`,
      status: 'on-track',
      trend: 'stable'
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{kpi ? 'Edit KPI' : 'Create New KPI'}</DialogTitle>
          <DialogDescription>
            {kpi ? 'Update the KPI details below' : 'Define a new Key Performance Indicator'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>KPI Name *</Label>
              <Input 
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Revenue Growth"
                required
              />
            </div>
            
            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea 
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of what this KPI measures"
                rows={2}
              />
            </div>

            <div>
              <Label>Perspective *</Label>
              <Select 
                value={formData.perspectiveId}
                onValueChange={(value) => setFormData({ ...formData, perspectiveId: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {bscPerspectives.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Entity *</Label>
              <Select 
                value={formData.entityId}
                onValueChange={(value) => {
                  const isCompany = mockCompanies.find(c => c.id === value);
                  setFormData({ 
                    ...formData, 
                    entityId: value,
                    entityType: value === 'enterprise' ? 'enterprise' : isCompany ? 'company' : 'function'
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                  {mockCompanies.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Measurement */}
          <div className="border-t border-border pt-4">
            <h4 className="font-medium text-foreground mb-3">Measurement</h4>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Unit</Label>
                <Select 
                  value={formData.unit}
                  onValueChange={(value) => setFormData({ ...formData, unit: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="%">Percentage (%)</SelectItem>
                    <SelectItem value="M">Million (M)</SelectItem>
                    <SelectItem value="K">Thousand (K)</SelectItem>
                    <SelectItem value="num">Number</SelectItem>
                    <SelectItem value="$">Currency ($)</SelectItem>
                    <SelectItem value="days">Days</SelectItem>
                    <SelectItem value="hrs">Hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Weight (%)</Label>
                <Input 
                  type="number"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: parseFloat(e.target.value) })}
                  min={0}
                  max={100}
                  step={0.5}
                />
              </div>

              <div>
                <Label>Annual Target</Label>
                <Input 
                  type="number"
                  value={formData.target}
                  onChange={(e) => setFormData({ ...formData, target: parseFloat(e.target.value) })}
                  step={0.1}
                />
              </div>

              <div>
                <Label>Current Actual</Label>
                <Input 
                  type="number"
                  value={formData.actual}
                  onChange={(e) => setFormData({ ...formData, actual: parseFloat(e.target.value) })}
                  step={0.1}
                />
              </div>
            </div>
          </div>

          {/* Formula & Data Source */}
          <div className="border-t border-border pt-4">
            <h4 className="font-medium text-foreground mb-3">Calculation & Source</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Formula</Label>
                <Input 
                  value={formData.formula}
                  onChange={(e) => setFormData({ ...formData, formula: e.target.value })}
                  placeholder="e.g., (Actual / Target) x 100"
                />
              </div>

              <div>
                <Label>Data Source</Label>
                <Input 
                  value={formData.dataSource}
                  onChange={(e) => setFormData({ ...formData, dataSource: e.target.value })}
                  placeholder="e.g., ERP System, Finance DB"
                />
              </div>

              <div>
                <Label>Owner</Label>
                <Input 
                  value={formData.owner}
                  onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                  placeholder="e.g., Finance Director"
                />
              </div>

              <div>
                <Label>Reporting Frequency</Label>
                <Select 
                  value={formData.frequency}
                  onValueChange={(value) => setFormData({ ...formData, frequency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annually">Annually</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit">{kpi ? 'Update KPI' : 'Create KPI'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const KPIManagement = ({ onSelectKPI }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPerspective, setSelectedPerspective] = useState('all');
  const [selectedEntity, setSelectedEntity] = useState('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingKPI, setEditingKPI] = useState(null);

  // Get all KPIs from mock data
  const allKPIs = useMemo(() => mockObjectives, []);

  // Filter KPIs
  const filteredKPIs = useMemo(() => {
    return allKPIs.filter(kpi => {
      const matchesSearch = kpi.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesPerspective = selectedPerspective === 'all' || kpi.perspectiveId === selectedPerspective;
      const matchesEntity = selectedEntity === 'all' || kpi.entityId === selectedEntity;
      return matchesSearch && matchesPerspective && matchesEntity;
    });
  }, [allKPIs, searchQuery, selectedPerspective, selectedEntity]);

  // Group KPIs by perspective
  const kpisByPerspective = useMemo(() => {
    const grouped = {};
    bscPerspectives.forEach(p => {
      grouped[p.id] = filteredKPIs.filter(kpi => kpi.perspectiveId === p.id);
    });
    return grouped;
  }, [filteredKPIs]);

  const handleSaveKPI = (kpiData) => {
    console.log('Saving KPI:', kpiData);
    // In real app, this would save to backend
  };

  const handleEditKPI = (kpi) => {
    setEditingKPI(kpi);
  };

  const handleDuplicateKPI = (kpi) => {
    setEditingKPI({ ...kpi, id: null, name: `${kpi.name} (Copy)` });
    setIsCreateDialogOpen(true);
  };

  const handleArchiveKPI = (kpi) => {
    console.log('Archiving KPI:', kpi.id);
    // In real app, this would archive the KPI
  };

  const getEntity = (entityId, entityType) => {
    if (entityType === 'company') {
      return mockCompanies.find(c => c.id === entityId);
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <SectionHeader
        title="KPI Management"
        icon={BarChart3}
        iconColor="text-purple-500"
        actions={
          <Button onClick={() => setIsCreateDialogOpen(true)} data-testid="create-kpi-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create KPI
          </Button>
        }
      >
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                placeholder="Search KPIs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          
          <Select value={selectedPerspective} onValueChange={setSelectedPerspective}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Perspectives" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Perspectives</SelectItem>
              {bscPerspectives.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedEntity} onValueChange={setSelectedEntity}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Entities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Entities</SelectItem>
              <SelectItem value="enterprise">Enterprise</SelectItem>
              {mockCompanies.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Badge variant="secondary" className="h-9 px-3">
            {filteredKPIs.length} KPIs
          </Badge>
        </div>
      </SectionHeader>

      {/* KPIs by Perspective */}
      {bscPerspectives.map(perspective => {
        const perspectiveKPIs = kpisByPerspective[perspective.id];
        if (perspectiveKPIs.length === 0) return null;
        
        const Icon = iconMap[perspective.icon] || Target;

        return (
          <div key={perspective.id}>
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${perspective.color}20` }}
              >
                <Icon className="w-5 h-5" style={{ color: perspective.color }} />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">{perspective.name}</h3>
                <p className="text-xs text-muted-foreground">{perspectiveKPIs.length} KPIs</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
              {perspectiveKPIs.map(kpi => (
                <KPICard 
                  key={kpi.id}
                  kpi={kpi}
                  perspective={perspective}
                  entity={getEntity(kpi.entityId, kpi.entityType)}
                  onClick={onSelectKPI}
                  onEdit={handleEditKPI}
                  onDuplicate={handleDuplicateKPI}
                  onArchive={handleArchiveKPI}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* Empty State */}
      {filteredKPIs.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-medium text-foreground">No KPIs Found</h3>
            <p className="text-muted-foreground mt-2">
              {searchQuery ? 'Try adjusting your search or filters' : 'Create your first KPI to get started'}
            </p>
            {!searchQuery && (
              <Button className="mt-4" onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create KPI
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <KPIDialog 
        open={isCreateDialogOpen || !!editingKPI}
        onClose={() => { setIsCreateDialogOpen(false); setEditingKPI(null); }}
        kpi={editingKPI}
        onSave={handleSaveKPI}
      />
    </div>
  );
};

export default KPIManagement;
